import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { auditsApi } from '../../services/audits';
import { brandQueriesApi } from '../../services/brand-queries';
import { competitorsApi } from '../../services/competitors';
import CompetitorDetailModal from './CompetitorDetailModal';
import DomainFavicon from './DomainFavicon';
import { useCredits } from '../../contexts/CreditContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatNumber } from '../../i18n/format';
import QuotaUpgradeDialog from '../billing/QuotaUpgradeDialog';
import {
  CREDIT_COST,
  resolveEntitlement,
  isBrandOwnedHost,
  sameEntityName,
  type BrandAxisAuditType,
  type CompetitorMatrix,
  type CompetitorCell,
  type QueryScoreEntry,
  type SuggestedCompetitor,
  type TrackedCompetitor,
} from '@traaker/shared';

// Narrative is intentionally omitted from the Competitors view: competitors are
// very unlikely to be co-cited alongside the brand, so the column was empty noise.
const AXES: { key: BrandAxisAuditType; labelKey: string; color: string }[] = [
  { key: 'brand_referral', labelKey: 'competitors.axis.referral', color: '#FF6B35' },
  { key: 'brand_authority', labelKey: 'competitors.axis.authority', color: '#00897B' },
];

/** i18n key per axis — resolved with `t()` at render. */
const AXIS_HINT_KEY: Record<BrandAxisAuditType, string> = {
  brand_referral: 'competitors.axisHint.referral',
  brand_authority: 'competitors.axisHint.authority',
  brand_narrative: 'competitors.axisHint.narrative',
  // Comparaison axis surfaces added by lot F
  brand_comparison: 'competitors.axisHint.narrative',
};

const ALL = '__all__';
/** Slice over the matrix prompts — filter by prompt language and (referral) zone. */
type Slice = { language: string; zone: string };

type AxisData = {
  latest: CompetitorMatrix | null;
  // domain/name → trend of "presentOn / promptCount" ratio across audits (newest last)
  trendByName: Record<string, number[]>;
  // Latest audit's per-prompt brand rollups, keyed by queryId — powers the brand
  // (⭐) column. Axis-appropriate fields: referral bestPosition/recommendedRate,
  // authority citationRate, narrative alignment.
  brandByQuery: Record<string, QueryScoreEntry>;
  completedAt?: string;
};

export default function CompetitorMatrixView({
  brandId,
  brandName: brandNameProp,
  brandDomain,
}: {
  brandId: string;
  brandName?: string;
  brandDomain?: string;
}) {
  const { t } = useTranslation();
  const brandName = brandNameProp || t('competitors.myBrand');
  const { isAgency } = useCredits();
  const { tenant } = useAuth();
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const competitorLimit = resolveEntitlement('competitors', tenant?.tier || 'basic').limit;
  const [axis, setAxis] = useState<BrandAxisAuditType>('brand_referral');
  const [byAxis, setByAxis] = useState<Record<string, AxisData>>({});
  // queryId → zone slug, joined from referral BrandQuery rows (the matrix prompt
  // doesn't carry zone — it's a referral-only facet on the query row).
  const [zoneByQuery, setZoneByQuery] = useState<Record<string, string>>({});
  const [slice, setSlice] = useState<Slice>({ language: ALL, zone: ALL });
  const [tracked, setTracked] = useState<TrackedCompetitor[]>([]);
  const [suggested, setSuggested] = useState<SuggestedCompetitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [detailFor, setDetailFor] = useState<{ domain: string; name: string } | null>(null);
  // name → resolved domain (Serper) for suggestions that didn't carry a sure
  // domain from a cited source. Lazy-filled; cross-tenant cached server-side.
  const [resolvedDomains, setResolvedDomains] = useState<Record<string, string>>({});
  const addFormRef = useRef<HTMLDivElement>(null);
  const domainInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [refM, authM, comps, refQueries] = await Promise.all([
        auditsApi.getAxisMatrix(brandId, 'brand_referral', 10),
        auditsApi.getAxisMatrix(brandId, 'brand_authority', 10),
        competitorsApi.list(brandId),
        // Zone lives on the referral BrandQuery row, not on the matrix prompt —
        // join it client-side by queryId for the zone filter.
        brandQueriesApi.list(brandId, 'referral').catch(() => []),
      ]);
      setByAxis({
        brand_referral: toAxisData(refM.audits),
        brand_authority: toAxisData(authM.audits),
      });
      const zoneMap: Record<string, string> = {};
      for (const q of refQueries) {
        if (q.zone) zoneMap[q.queryId] = q.zone;
      }
      setZoneByQuery(zoneMap);
      setTracked(comps);
      // Suggestions come from the latest referral audit; dedup against tracked
      // names. `sameEntityName`, not fold-equality: the engines write "NATHALIE
      // NIVET INTÉRIEUR DESIGN" for a competitor tracked as "Nathalienivet
      // design", which was offered back as a new one to track (2026-09-30).
      // Doing it here also cleans suggestions already persisted in old summaries.
      const latestRef = refM.audits.find((a) => (a.suggestedCompetitors?.length ?? 0) > 0);
      setSuggested(
        (latestRef?.suggestedCompetitors || []).filter(
          (s) => !comps.some((c) => sameEntityName(c.name, s.name)),
        ),
      );
    } catch (err: any) {
      setError(err?.message || t('competitors.error.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (brandId) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Lazily resolve domains for suggestions that didn't carry a sure (cited) domain.
  // Sequential to be gentle on Serper; results cached server-side cross-tenant.
  useEffect(() => {
    if (!brandId || suggested.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const s of suggested) {
        if (s.domain || resolvedDomains[foldName(s.name)]) continue;
        try {
          const { domain } = await competitorsApi.resolveDomain(brandId, s.name);
          if (cancelled) return;
          if (domain) setResolvedDomains((prev) => ({ ...prev, [foldName(s.name)]: domain }));
        } catch {
          /* leave unresolved → letter avatar; not fatal */
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggested, brandId]);

  /** Best domain for a suggestion: sure (cited) → resolved (Serper) → guess. */
  const suggestionDomain = (s: SuggestedCompetitor): string | undefined =>
    s.domain || resolvedDomains[foldName(s.name)] || undefined;

  // Hide suggestions that are actually OUR OWN brand (the engine named the brand
  // itself — name-fold exclusion in the agent misses spelling variants like
  // "Growth Room" vs "GrowthRoom", so we also drop by DOMAIN here: bulletproof
  // since the domain is unique). Ownership is `isBrandOwnedHost`, not `===`: a
  // subdomain (support-fr.sodastream.com) or a sibling TLD (sodastream.com for a
  // brand stored sodastream.fr) is still us, and was being offered as a
  // competitor to track (fixed 2026-09-04). Filtering here also cleans up
  // suggestions already persisted in older audit summaries. Also drop anything
  // already tracked.
  const brandDom = normalizeDomain(brandDomain);
  const trackedDoms = new Set(tracked.map((c) => c.domain));
  const visibleSuggestions = suggested.filter((s) => {
    const d = suggestionDomain(s);
    if (d && brandDom && isBrandOwnedHost(normalizeDomain(d), brandDom)) return false;
    if (d && trackedDoms.has(d)) return false;
    return true;
  });

  const handleAdd = async () => {
    if (!newName.trim() || !isValidDomain(newDomain)) return;
    setAdding(true);
    setError('');
    try {
      // Domain is required on the manual path: it keys the favicon + the shared
      // DomainProfile (news/source tracking). A wrong guess would poison both.
      await competitorsApi.add(brandId, {
        name: newName.trim(),
        domain: newDomain.trim(),
      });
      setNewName('');
      setNewDomain('');
      setTracked(await competitorsApi.list(brandId));
    } catch (err: any) {
      if (err?.body?.code === 'QUOTA_EXCEEDED' && err?.body?.metric === 'competitors') {
        setQuotaDialogOpen(true);
      } else {
        setError(err?.message || t('competitors.error.add'));
      }
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (domain: string) => {
    setError('');
    try {
      await competitorsApi.remove(brandId, domain);
      setTracked(await competitorsApi.list(brandId));
    } catch (err: any) {
      setError(err?.message || t('competitors.error.remove'));
    }
  };

  const handleTrackSuggested = async (s: SuggestedCompetitor) => {
    // Add DIRECTLY: the suggestion's domain was already validated (cited in a
    // source or Serper-resolved + favicon-fetched), so the prefill-and-confirm
    // step is needless friction. If the domain happens to be wrong, the user
    // removes + re-adds manually. When we have NO domain at all, fall back to the
    // prefill flow so the user supplies one (we never persist a blind guess).
    const domain = suggestionDomain(s);
    if (!domain) {
      setError('');
      setNewName(s.name);
      setNewDomain(guessDomain(s.name));
      requestAnimationFrame(() => {
        addFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        domainInputRef.current?.focus();
        domainInputRef.current?.select();
      });
      return;
    }
    setError('');
    try {
      await competitorsApi.add(brandId, { name: s.name, domain });
      const comps = await competitorsApi.list(brandId);
      setTracked(comps);
      setSuggested((prev) =>
        prev.filter((x) => !comps.some((c) => sameEntityName(c.name, x.name))),
      );
    } catch (err: any) {
      if (err?.body?.code === 'QUOTA_EXCEEDED' && err?.body?.metric === 'competitors') {
        setQuotaDialogOpen(true);
      } else {
        setError(err?.message || t('competitors.error.add'));
      }
    }
  };

  // Available facet values for the dropdowns. Language comes from the current
  // axis's prompts; zone is a referral-only facet (joined from query rows), so the
  // zone dropdown only appears on the referral tab.
  const languageOptions = useMemo(() => {
    const set = new Set<string>();
    // Language facet is axis-independent — union across every axis's prompts —
    // so the top filter bar governs BOTH the (referral-ranked) synthèse table and
    // the matrix regardless of which axis tab is active.
    for (const a of AXES) {
      for (const p of byAxis[a.key]?.latest?.prompts || []) {
        if (p.language) set.add(p.language);
      }
    }
    return [...set].sort();
  }, [byAxis]);
  const zoneOptions = useMemo(() => {
    // Zone is a referral-only facet, but the synthèse table ranks by referral PdV
    // so it's always relevant here — surface it independently of the active tab.
    const set = new Set<string>(Object.values(zoneByQuery));
    return [...set].sort();
  }, [zoneByQuery]);

  // Slice every axis's data to the selected (language, zone): filter the matrix
  // prompts, recompute the footer rollups + brand per-prompt map over the subset,
  // so BOTH the matrix and the ranked "Concurrents suivis" table re-order for the
  // chosen slice. Zone only constrains referral (other axes have no zone).
  const slicedByAxis = useMemo(() => {
    const out: Record<string, AxisData> = {};
    for (const a of AXES) {
      out[a.key] = sliceAxisData(byAxis[a.key], slice, zoneByQuery, a.key === 'brand_referral');
    }
    return out;
  }, [byAxis, slice, zoneByQuery]);

  const data = slicedByAxis[axis];
  // Filter the matrix columns to the CURRENTLY-tracked set so add/remove reflects
  // instantly (the stored matrix.competitors is frozen at audit time — a removed
  // competitor would otherwise linger as a column until the next audit; a newly
  // added one shows as a column with all-absent cells until its first audit).
  const matrix = useMemo(() => {
    const m = data?.latest || null;
    if (!m) return null;
    const trackedFold = new Set(tracked.map((c) => foldName(c.name)));
    const cols = m.competitors.filter((c) => trackedFold.has(foldName(c)));
    // Append freshly-tracked names not yet in the audit (all-absent column).
    for (const t of tracked) {
      if (!cols.some((c) => foldName(c) === foldName(t.name))) cols.push(t.name);
    }
    return { ...m, competitors: cols };
  }, [data, tracked]);
  const meta = AXES.find((a) => a.key === axis)!;
  const sliceActive = slice.language !== ALL || slice.zone !== ALL;

  return (
    <Box>
      {/* Tracked-competitor synthesis table (per-axis metrics + 90j trend) */}
      <Card sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, pb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('competitors.tracked.title', { count: tracked.length })}
          </Typography>
          {/* Agency credit cost: each tracked competitor is probed once per
              Brand Referral run (2 credits), on top of the engine-call cost.
              Surfaced here — competitors are managed on this page, not in settings. */}
          {isAgency && tracked.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('competitors.tracked.creditCost', {
                total: formatNumber(tracked.length * CREDIT_COST.competitor),
                count: tracked.length,
                unit: CREDIT_COST.competitor,
              })}
            </Typography>
          )}
        </Box>

        {/* Filter bar — slices the referral-PdV ranking below AND the matrix.
            Each dropdown auto-hides when its facet has ≤1 value. */}
        {tracked.length > 0 && (languageOptions.length > 1 || zoneOptions.length > 1) && (
          <FilterBar
            slice={slice}
            setSlice={setSlice}
            languageOptions={languageOptions}
            zoneOptions={zoneOptions}
            sliceActive={sliceActive}
          />
        )}

        {tracked.length === 0 ? (
          <Box sx={{ px: 2.5, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('competitors.tracked.empty')}
            </Typography>
          </Box>
        ) : (
          <TrackedTable
            tracked={tracked}
            byAxis={slicedByAxis}
            onSelect={(domain, name) => setDetailFor({ domain, name })}
            onRemove={handleRemove}
          />
        )}

        {/* Add row — align field tops (flex-start) so the domain helperText hangs
            below without pushing Nom + the button down. */}
        <Box ref={addFormRef} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', p: 2.5, pt: 2 }}>
          <TextField
            size="small"
            label={t('competitors.add.name')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            required
            label={t('competitors.add.domain')}
            placeholder={t('competitors.add.domainPlaceholder')}
            inputRef={domainInputRef}
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            error={newDomain.trim().length > 0 && !isValidDomain(newDomain)}
            helperText={
              newDomain.trim().length > 0 && !isValidDomain(newDomain)
                ? t('competitors.add.domainInvalid')
                : t('competitors.add.domainHelper')
            }
            sx={{ width: 220 }}
          />
          {/* Button height matches the input box (size=small ≈ 40px) so their tops align. */}
          <Button
            variant="contained"
            startIcon={adding ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            onClick={handleAdd}
            disabled={adding || !newName.trim() || !isValidDomain(newDomain)}
            sx={{ height: 40 }}
          >
            {t('competitors.add.submit')}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
            {t('competitors.add.scoresHint')}
          </Typography>
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ px: 2.5, pb: 2, display: 'block' }}>
            {error}
          </Typography>
        )}
      </Card>

      {/* Matrix */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Axis tabs */}
          <Box sx={{ display: 'flex', gap: 0.5, p: 2, pb: 1 }}>
            {AXES.map((a) => (
              <Button
                key={a.key}
                size="small"
                onClick={() => setAxis(a.key)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  bgcolor: axis === a.key ? a.color : 'transparent',
                  color: axis === a.key ? '#fff' : 'text.secondary',
                  '&:hover': { bgcolor: axis === a.key ? a.color : 'rgba(0,0,0,0.04)' },
                }}
              >
                {t(a.labelKey)}
              </Button>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1.5 }}>
            {t(AXIS_HINT_KEY[axis])}
          </Typography>

          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : !matrix || matrix.prompts.length === 0 || tracked.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {tracked.length === 0
                  ? t('competitors.matrix.emptyNoCompetitors')
                  : t('competitors.matrix.emptyNoAudit')}
              </Typography>
            </Box>
          ) : (
            <MatrixTable
              matrix={matrix}
              axis={axis}
              accent={meta.color}
              brandName={brandName}
              brandDomain={brandDomain}
              brandByQuery={data?.brandByQuery || {}}
              domainByName={Object.fromEntries(tracked.map((c) => [foldName(c.name), c.domain]))}
              onSelect={(name) => {
                const t = tracked.find((c) => foldName(c.name) === foldName(name));
                if (t) setDetailFor({ domain: t.domain, name: t.name });
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Suggested competitors (Class A §4 — untracked brands the engines recommend) */}
      {visibleSuggestions.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {t('competitors.suggested.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('competitors.suggested.subtitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {visibleSuggestions.map((s) => (
                <Box
                  key={s.name}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    py: 1, px: 1.5, borderRadius: 1.5, bgcolor: 'rgba(0,0,0,0.02)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    {/* Favicon uses the sure (cited) domain, else the Serper-resolved
                        one once it lands; letter avatar until then. */}
                    <DomainFavicon domain={suggestionDomain(s)} name={s.name} size={28} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('competitors.suggested.citedIn', { citedIn: s.citedIn, promptCount: s.promptCount })}
                        {suggestionDomain(s) ? ` · ${suggestionDomain(s)}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleTrackSuggested(s)}
                  >
                    {t('competitors.suggested.follow')}
                  </Button>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <CompetitorDetailModal
        brandId={brandId}
        brandName={brandName}
        domain={detailFor?.domain ?? null}
        name={detailFor?.name ?? ''}
        open={!!detailFor}
        metrics={detailFor ? buildCompetitorMetrics(detailFor.name, slicedByAxis) : null}
        onClose={() => setDetailFor(null)}
      />

      <QuotaUpgradeDialog
        open={quotaDialogOpen}
        onClose={() => setQuotaDialogOpen(false)}
        highlightMetric="competitors"
        body={t('competitors.quota.body', { limit: competitorLimit })}
      />
    </Box>
  );
}

/**
 * Per-competitor metrics for the detail modal (mock-parity bandeau + synthesis +
 * opportunities). Pure aggregation over the already-loaded `byAxis` data — no
 * extra fetch. Opportunities compare the brand's cell vs the competitor's cell
 * per prompt (attack = competitor present & we're weak; defend = both present).
 */
export type CompetitorMetrics = {
  referral: { pct: number | null; trend: number[]; recommendedOn: number; promptCount: number; avgPosition?: number };
  authority: { pct: number | null; presentOn: number; promptCount: number };
  narrative: { coCited: number; promptCount: number };
  opportunities: { kind: 'attack' | 'defend'; axis: BrandAxisAuditType; prompt: string; detail: string }[];
};

function buildCompetitorMetrics(name: string, byAxis: Record<string, AxisData>): CompetitorMetrics {
  const ref = byAxis['brand_referral'];
  const auth = byAxis['brand_authority'];
  const narr = byAxis['brand_narrative'];

  const refFoot = ref?.latest?.footer?.[name];
  const authFoot = auth?.latest?.footer?.[name];
  const narrFoot = narr?.latest?.footer?.[name];

  const referral = {
    pct: refFoot && refFoot.promptCount ? Math.round((refFoot.presentOn / refFoot.promptCount) * 100) : null,
    trend: ref?.trendByName?.[name] || [],
    recommendedOn: refFoot?.recommendedOn ?? 0,
    promptCount: refFoot?.promptCount ?? 0,
    avgPosition: refFoot?.avgPosition,
  };
  const authority = {
    pct: authFoot && authFoot.promptCount ? Math.round((authFoot.presentOn / authFoot.promptCount) * 100) : null,
    presentOn: authFoot?.presentOn ?? 0,
    promptCount: authFoot?.promptCount ?? 0,
  };
  const narrative = { coCited: narrFoot?.presentOn ?? 0, promptCount: narrFoot?.promptCount ?? 0 };

  // Opportunities: scan referral prompts, compare brand cell vs competitor cell.
  const opportunities: CompetitorMetrics['opportunities'] = [];
  const refMatrix = ref?.latest;
  const refBrand = ref?.brandByQuery || {};
  if (refMatrix) {
    for (const p of refMatrix.prompts) {
      const compCell = p.cells[name];
      if (!compCell || compCell.state === 'absent') continue;
      const brand = refBrand[p.queryId];
      const compPos = compCell.state === 'position' ? compCell.position : undefined;
      const brandPos = brand?.bestPosition;
      // Attack: competitor ranked, brand absent or clearly behind.
      if (compPos && (!brandPos || brandPos > compPos)) {
        opportunities.push({
          kind: 'attack', axis: 'brand_referral', prompt: p.queryText,
          detail: brandPos
            ? i18n.t('competitors.modal.opportunities.detailAttackBehind', { compPos, brandPos })
            : i18n.t('competitors.modal.opportunities.detailAttackAbsent', { compPos }),
        });
      } else if (compPos && brandPos && brandPos <= compPos) {
        // Defend: brand ahead/tied but competitor is right behind.
        opportunities.push({
          kind: 'defend', axis: 'brand_referral', prompt: p.queryText,
          detail: i18n.t('competitors.modal.opportunities.detailDefend', { compPos, brandPos }),
        });
      }
    }
  }
  // Cap to keep the modal tidy; attacks first.
  opportunities.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'attack' ? -1 : 1));

  return { referral, authority, narrative, opportunities: opportunities.slice(0, 6) };
}

/** Accent-fold + lowercase for dedup against tracked names (mirrors backend). */
function foldName(s: string): string {
  return (s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Best-effort domain guess from a brand name for suggestion-add (the engines give
 * a name, not a domain). Takes the first token, strips non-alphanumerics, appends
 * `.com`. Right for most single-word SaaS (HubSpot\u2192hubspot.com, Pipedrive\u2192
 * pipedrive.com); wrong for multi-word / non-.com \u2014 the user re-adds with the
 * explicit domain field to correct. Backend re-normalizes regardless.
 */
function guessDomain(name: string): string {
  const first = (name || '').trim().split(/[\s/|,]/)[0] || '';
  const slug = foldName(first).replace(/[^a-z0-9]/g, '');
  return slug ? `${slug}.com` : '';
}

/** Loose domain check for the manual add field: at least one dot, a TLD ≥2 chars,
 *  no spaces. Strips an accidental scheme/path the user might paste. */
function isValidDomain(input: string): boolean {
  let d = (input || '').trim().toLowerCase();
  if (!d) return false;
  d = d.replace(/^https?:\/\//, '').split('/')[0];
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d) && /\.[a-z]{2,}$/.test(d);
}

// ─── Filter bar (language + referral zone) ──────────────────────────────────

function FilterBar({
  slice,
  setSlice,
  languageOptions,
  zoneOptions,
  sliceActive,
}: {
  slice: Slice;
  setSlice: React.Dispatch<React.SetStateAction<Slice>>;
  languageOptions: string[];
  zoneOptions: string[];
  sliceActive: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', px: 2.5, pb: 1.5, flexWrap: 'wrap' }}>
      {languageOptions.length > 1 && (
        <TextField
          select
          size="small"
          label={t('competitors.filter.language')}
          value={slice.language}
          onChange={(e) => setSlice((s) => ({ ...s, language: e.target.value }))}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value={ALL}>{t('competitors.filter.allLanguages')}</MenuItem>
          {languageOptions.map((l) => (
            <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>
          ))}
        </TextField>
      )}
      {zoneOptions.length > 1 && (
        <TextField
          select
          size="small"
          label={t('competitors.filter.zone')}
          value={slice.zone}
          onChange={(e) => setSlice((s) => ({ ...s, zone: e.target.value }))}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value={ALL}>{t('competitors.filter.allZones')}</MenuItem>
          {zoneOptions.map((z) => (
            <MenuItem key={z} value={z}>{z}</MenuItem>
          ))}
        </TextField>
      )}
      {sliceActive && (
        <Button size="small" onClick={() => setSlice({ language: ALL, zone: ALL })} sx={{ textTransform: 'none' }}>
          {t('competitors.filter.reset')}
        </Button>
      )}
    </Box>
  );
}

// ─── Tracked-competitor synthesis table (mock parity) ───────────────────────

function MiniSpark({ points, color }: { points: number[]; color: string }) {
  if (!points || points.length < 2) return <Box sx={{ height: 22 }} />;
  const w = 64, h = 22, max = Math.max(...points, 1);
  const stepX = w / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * stepX).toFixed(1)},${(h - (p / max) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <path d={d} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function Metric({ value, delta, color }: { value: string; delta?: number; color: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, justifyContent: 'flex-end' }}>
      <Typography variant="body2" fontWeight={700} sx={{ color }}>{value}</Typography>
      {delta !== undefined && delta !== 0 && (
        <Typography variant="caption" sx={{ color: delta > 0 ? '#15803D' : '#B91C1C', fontWeight: 600 }}>
          {delta > 0 ? '+' : ''}{delta}
        </Typography>
      )}
    </Box>
  );
}

function TrackedTable({
  tracked,
  byAxis,
  onSelect,
  onRemove,
}: {
  tracked: TrackedCompetitor[];
  byAxis: Record<string, AxisData>;
  onSelect: (domain: string, name: string) => void;
  onRemove: (domain: string) => void;
}) {
  const { t } = useTranslation();
  const ref = byAxis['brand_referral'];
  const auth = byAxis['brand_authority'];

  const headCell = { textAlign: 'left' as const, p: 1.5, fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' as const, letterSpacing: '0.04em', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#FAFBFD' };
  const numHead = { ...headCell, textAlign: 'right' as const };

  // Rank by Referral share-of-voice (PdV) descending — #1 = most-recommended by
  // the AI engines. Competitors with no referral data sort last (PdV = -1).
  const ranked = tracked
    .map((c) => {
      const refFoot = ref?.latest?.footer?.[c.name];
      const refPct = refFoot && refFoot.promptCount
        ? Math.round((refFoot.presentOn / refFoot.promptCount) * 100)
        : null;
      return { c, refFoot, refPct, sortKey: refPct ?? -1 };
    })
    .sort((a, b) => b.sortKey - a.sortKey);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 760 }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={{ ...headCell, textAlign: 'center', width: 44 }}>#</Box>
            <Box component="th" sx={headCell}>{t('competitors.table.competitor')}</Box>
            <Box component="th" sx={numHead}>{t('competitors.table.referralShare')}</Box>
            <Box component="th" sx={numHead}>{t('competitors.table.authority')}</Box>
            <Box component="th" sx={{ ...headCell, textAlign: 'center' }}>{t('competitors.table.trend')}</Box>
            <Box component="th" sx={headCell}></Box>
          </Box>
        </Box>
        <Box component="tbody">
          {ranked.map(({ c, refPct }, idx) => {
            const authScore = scoreForName(auth, c.name);
            const refTrend = ref?.trendByName?.[c.name] || [];
            const rank = idx + 1;

            return (
              <Box
                component="tr"
                key={c.domain}
                onClick={() => onSelect(c.domain, c.name)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box component="td" sx={{ p: 1.5, textAlign: 'center' }}>
                  <RankBadge rank={rank} hasScore={refPct !== null} />
                </Box>
                <Box component="td" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DomainFavicon domain={c.domain} name={c.name} size={32} />
                    <Box>
                      <Typography variant="body2" fontWeight={700}>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.domain}</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box component="td" sx={{ p: 1.5 }}>
                  {refPct !== null ? <Metric value={`${refPct}%`} color="#B45309" /> : <Dash />}
                </Box>
                <Box component="td" sx={{ p: 1.5 }}>
                  {authScore !== null ? <Metric value={String(authScore)} color="#047857" /> : <Dash />}
                </Box>
                <Box component="td" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Box sx={{ display: 'inline-block' }}><MiniSpark points={refTrend} color="#FF6B35" /></Box>
                </Box>
                <Box component="td" sx={{ p: 1.5, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Typography component="span" variant="caption" sx={{ color: 'var(--mui-palette-primary-main, #7C3AED)', fontWeight: 600, mr: 1 }}>
                    {t('competitors.table.detail')}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onRemove(c.domain); }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function Dash() {
  return <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'right' }}>—</Typography>;
}

/** Rank chip for the tracked table (by Referral PdV). Top 3 get an accent;
 *  competitors with no referral data render a muted dash instead of a rank. */
function RankBadge({ rank, hasScore }: { rank: number; hasScore: boolean }) {
  if (!hasScore) return <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;
  const top = rank <= 3;
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 26, height: 22, px: 0.75, borderRadius: '6px',
        fontSize: 12, fontWeight: 700,
        bgcolor: top ? '#FFF1EC' : '#F1F5F9',
        color: top ? '#C2410C' : '#5B6479',
      }}
    >
      #{rank}
    </Box>
  );
}

/** Authority "score" per competitor — approximated from present-on ratio ×100
 *  (the matrix footer doesn't carry the deterministic authority score per
 *  competitor; present-on is the available proxy and matches the column intent). */
function scoreForName(axisData: AxisData | undefined, name: string): number | null {
  const f = axisData?.latest?.footer?.[name];
  if (!f || !f.promptCount) return null;
  return Math.round((f.presentOn / f.promptCount) * 100);
}

// ─── Matrix table ─────────────────────────────────────────────────────────

function MatrixTable({
  matrix,
  axis,
  accent,
  brandName,
  brandDomain,
  brandByQuery,
  domainByName,
  onSelect,
}: {
  matrix: CompetitorMatrix;
  axis: BrandAxisAuditType;
  accent: string;
  brandName: string;
  brandDomain?: string;
  brandByQuery: Record<string, QueryScoreEntry>;
  domainByName: Record<string, string>;
  onSelect: (name: string) => void;
}) {
  const { t } = useTranslation();
  const competitors = matrix.competitors;
  const BRAND_BG = '#E6FBF6'; // teal tint — distinguishes the ⭐ brand column
  return (
    <Box sx={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }}>
      <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 720 }}>
        <Box component="thead">
          <Box component="tr">
            <Box
              component="th"
              sx={{
                position: 'sticky', left: 0, top: 0, zIndex: 3, bgcolor: 'background.paper',
                textAlign: 'left', p: 1.5, minWidth: 280, borderBottom: '2px solid', borderColor: 'divider',
                fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary',
              }}
            >
              {t('competitors.matrix.activePrompt')}
            </Box>
            {/* ⭐ Brand column — your own performance, for side-by-side comparison */}
            <Box
              component="th"
              sx={{
                position: 'sticky', top: 0, zIndex: 2, bgcolor: BRAND_BG,
                p: 1.5, borderBottom: '2px solid', borderColor: 'divider',
                fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', color: '#047857',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                <DomainFavicon domain={normalizeDomain(brandDomain)} name={brandName} size={20} />
                ⭐ {brandName}
              </Box>
            </Box>
            {competitors.map((c) => (
              <Box
                key={c}
                component="th"
                onClick={() => onSelect(c)}
                sx={{
                  position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper',
                  p: 1.5, borderBottom: '2px solid', borderColor: 'divider',
                  fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                  cursor: 'pointer', '&:hover': { color: accent, textDecoration: 'underline' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                  <DomainFavicon domain={domainByName[foldName(c)]} name={c} size={20} />
                  {c}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {matrix.prompts.map((p) => (
            <Box component="tr" key={p.queryId}>
              <Box
                component="td"
                sx={{
                  position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper',
                  p: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                  borderRight: '1px solid', maxWidth: 280,
                }}
              >
                <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.3 }}>
                  {p.queryText}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  {p.level && <Chip label={p.level} size="small" sx={{ height: 16, fontSize: 9 }} />}
                  {p.language && <Chip label={p.language.toUpperCase()} size="small" sx={{ height: 16, fontSize: 9 }} />}
                </Box>
              </Box>
              {/* ⭐ Brand cell — derived from the brand's own per-prompt rollup */}
              <Box component="td" sx={{ p: 1, textAlign: 'center', bgcolor: BRAND_BG, borderBottom: '1px solid', borderColor: 'divider' }}>
                <BrandCell entry={brandByQuery[p.queryId]} axis={axis} />
              </Box>
              {competitors.map((c) => (
                <Box
                  key={c}
                  component="td"
                  sx={{ p: 1, textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <CellChip cell={p.cells[c]} />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
        {/* Footer rollups */}
        <Box component="tfoot">
          <Box component="tr">
            <Box
              component="td"
              sx={{
                position: 'sticky', left: 0, zIndex: 1, bgcolor: 'rgba(0,0,0,0.02)',
                p: 1.5, borderTop: '2px solid', borderColor: 'divider', fontWeight: 700,
                fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary',
              }}
            >
              {axis === 'brand_referral'
                ? t('competitors.matrix.footerRecommended')
                : axis === 'brand_narrative'
                ? t('competitors.matrix.footerCocited')
                : t('competitors.matrix.footerPresent')}
            </Box>
            {/* ⭐ Brand footer — its own present/recommended count */}
            <Box component="td" sx={{ p: 1, textAlign: 'center', bgcolor: BRAND_BG, borderTop: '2px solid', borderColor: 'divider' }}>
              <Typography variant="body2" fontWeight={700} sx={{ color: '#047857' }}>
                {brandFooterText(brandByQuery, axis)}
              </Typography>
            </Box>
            {competitors.map((c) => {
              const f = matrix.footer[c];
              const numer = axis === 'brand_referral' ? f?.recommendedOn ?? 0 : f?.presentOn ?? 0;
              return (
                <Box
                  key={c}
                  component="td"
                  sx={{ p: 1, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.02)', borderTop: '2px solid', borderColor: 'divider' }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    {numer}/{f?.promptCount ?? 0}
                  </Typography>
                  {axis === 'brand_referral' && f?.avgPosition != null && (
                    <Typography variant="caption" sx={{ color: accent }}>
                      {t('competitors.matrix.avgPosition', { position: f.avgPosition })}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * The brand's own cell, derived from its per-prompt QueryScoreEntry (not the
 * competitor matrix). Axis-appropriate: referral → best position / recommended,
 * authority → cited rate as source/cité, narrative → alignment label.
 */
function BrandCell({ entry, axis }: { entry?: QueryScoreEntry; axis: BrandAxisAuditType }) {
  const { t } = useTranslation();
  if (!entry) return <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;

  if (axis === 'brand_referral') {
    if (entry.bestPosition) {
      const { bg, fg } = positionColor(entry.bestPosition);
      return <Chip label={`#${entry.bestPosition}`} size="small" sx={{ bgcolor: bg, color: fg, fontWeight: 700, height: 24 }} />;
    }
    // Presence floor (mirrors scorer.compute_axis_score referral): the brand is
    // only "cité" if it's actually NAMED in a response (citedRate > 0). A
    // recommendedRate > 0 WITHOUT a citation is the known scorer false-positive
    // (the Previfrance hallucination — a competitor's answer mis-attributed as
    // "recommended"). Don't show "cité" on recommendedRate alone.
    if ((entry.citedRate ?? 0) > 0) {
      return <Chip label={t('competitors.cell.cited')} size="small" sx={{ bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 600, height: 24 }} />;
    }
    return <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;
  }

  if (axis === 'brand_authority') {
    const rate = entry.citationRate ?? 0;
    if (rate >= 50) return <Chip label={t('competitors.cell.source')} size="small" sx={{ bgcolor: '#D1FAE5', color: '#047857', fontWeight: 700, height: 24 }} />;
    if (rate > 0) return <Chip label={t('competitors.cell.cited')} size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 600, height: 24 }} />;
    return <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;
  }

  // narrative → alignment label; classified rows may have no alignment — show dash
  const a = entry.alignment;
  if (!a) return <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;
  const map: Record<string, { labelKey: string; bg: string; fg: string }> = {
    strong: { labelKey: 'competitors.alignment.strong', bg: '#DCFCE7', fg: '#15803D' },
    partial: { labelKey: 'competitors.alignment.partial', bg: '#FEF9C3', fg: '#A16207' },
    weak: { labelKey: 'competitors.alignment.weak', bg: '#FEF3C7', fg: '#B45309' },
    misaligned: { labelKey: 'competitors.alignment.misaligned', bg: '#FEE2E2', fg: '#B91C1C' },
  };
  const m = map[a] ?? map.weak;
  return <Chip label={t(m.labelKey)} size="small" sx={{ bgcolor: m.bg, color: m.fg, fontWeight: 600, height: 24 }} />;
}

/** Brand footer summary: how many prompts the brand is present/recommended on. */
export function brandFooterText(brandByQuery: Record<string, QueryScoreEntry>, axis: BrandAxisAuditType): string {
  // Narrative (F step 8d): a classified entry without `alignment` was not measured, so
  // it is out of the denominator too (unclassified entries always carry one).
  const byAlignment = axis !== 'brand_referral' && axis !== 'brand_authority';
  const entries = Object.values(brandByQuery).filter((e) => !byAlignment || e.alignment);
  const total = entries.length;
  if (total === 0) return '—';
  let n = 0;
  for (const e of entries) {
    if (axis === 'brand_referral') {
      if (e.bestPosition || (e.recommendedRate ?? 0) > 0) n++;
    } else if (axis === 'brand_authority') {
      if ((e.citationRate ?? 0) > 0) n++;
    } else {
      if (e.alignment && e.alignment !== 'misaligned' && e.alignment !== 'weak') n++;
    }
  }
  return `${n}/${total}`;
}

/** lowercase, strip scheme/path/www — mirror of the backend domain normalizer. */
function normalizeDomain(input?: string): string | undefined {
  if (!input) return undefined;
  let d = input.trim().toLowerCase().replace(/^https?:\/\//, '');
  d = d.split('/')[0].split('?')[0].replace(/^www\./, '');
  return d || undefined;
}

function CellChip({ cell }: { cell?: CompetitorCell }) {
  const { t } = useTranslation();
  const state = cell?.state || 'absent';
  if (state === 'position') {
    const pos = cell?.position ?? 0;
    const { bg, fg } = positionColor(pos);
    return <Chip label={`#${pos}`} size="small" sx={{ bgcolor: bg, color: fg, fontWeight: 700, height: 24 }} />;
  }
  if (state === 'cited') {
    return <Chip label={t('competitors.cell.cited')} size="small" sx={{ bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 600, height: 24 }} />;
  }
  if (state === 'cocited') {
    return <Chip label={t('competitors.cell.cocited')} size="small" sx={{ bgcolor: '#F3E8FF', color: '#6B21A8', fontWeight: 600, height: 24 }} />;
  }
  return <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>;
}

function positionColor(pos: number): { bg: string; fg: string } {
  if (pos === 1) return { bg: '#DCFCE7', fg: '#15803D' };
  if (pos <= 3) return { bg: '#FEF9C3', fg: '#A16207' };
  if (pos <= 5) return { bg: '#FEF3C7', fg: '#B45309' };
  return { bg: '#FEE2E2', fg: '#B91C1C' };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Restrict an axis's data to the selected (language, zone) slice. Filters the
 * latest matrix's prompts, recomputes the footer rollups over the subset, and
 * narrows `brandByQuery` to the surviving prompts — so the ranked table, matrix,
 * and brand column all re-order for the slice. Zone only applies to referral
 * (other axes carry no zone — `applyZone=false` skips it). Returns the data
 * unchanged when no slice is active (cheap identity path).
 */
function sliceAxisData(
  data: AxisData | undefined,
  slice: Slice,
  zoneByQuery: Record<string, string>,
  applyZone: boolean,
): AxisData {
  if (!data) return { latest: null, trendByName: {}, brandByQuery: {} };
  const filterLang = slice.language !== ALL;
  const filterZone = applyZone && slice.zone !== ALL;
  if (!filterLang && !filterZone) return data;
  const m = data.latest;
  if (!m) return data;

  const prompts = m.prompts.filter((p) => {
    if (filterLang && (p.language || '') !== slice.language) return false;
    if (filterZone && (zoneByQuery[p.queryId] || '') !== slice.zone) return false;
    return true;
  });

  // Recompute footer rollups over the surviving prompts.
  const footer: CompetitorMatrix['footer'] = {};
  for (const name of m.competitors) {
    let presentOn = 0, recommendedOn = 0, promptCount = 0;
    const positions: number[] = [];
    for (const p of prompts) {
      promptCount++;
      const cell = p.cells[name];
      if (cell && cell.state !== 'absent') presentOn++;
      if (cell?.state === 'position') {
        recommendedOn++;
        if (cell.position) positions.push(cell.position);
      }
    }
    footer[name] = {
      presentOn,
      promptCount,
      recommendedOn,
      ...(positions.length
        ? { avgPosition: Math.round((positions.reduce((a, b) => a + b, 0) / positions.length) * 10) / 10 }
        : {}),
    };
  }

  // Narrow the brand per-prompt map to surviving prompts.
  const keep = new Set(prompts.map((p) => p.queryId));
  const brandByQuery: Record<string, QueryScoreEntry> = {};
  for (const [qid, qs] of Object.entries(data.brandByQuery)) {
    if (keep.has(qid)) brandByQuery[qid] = qs;
  }

  return {
    latest: { ...m, prompts, footer },
    trendByName: data.trendByName, // trend stays full-history (not sliced)
    brandByQuery,
    completedAt: data.completedAt,
  };
}

function toAxisData(
  audits: {
    completedAt: string;
    competitorMatrix?: CompetitorMatrix | null;
    queryScores?: QueryScoreEntry[];
  }[],
): AxisData {
  // audits are newest-first from the API
  const withMatrix = audits.filter((a) => a.competitorMatrix);
  const latest = (withMatrix[0]?.competitorMatrix as CompetitorMatrix) || null;
  const trendByName: Record<string, number[]> = {};
  // Build oldest→newest present-ratio trend per competitor name.
  const chrono = [...withMatrix].reverse();
  for (const a of chrono) {
    const m = a.competitorMatrix as CompetitorMatrix;
    for (const name of m.competitors) {
      const f = m.footer[name];
      const ratio = f && f.promptCount ? Math.round((f.presentOn / f.promptCount) * 100) : 0;
      (trendByName[name] ||= []).push(ratio);
    }
  }
  // Brand's own per-prompt rollups from the same (latest) audit, keyed by queryId.
  const brandByQuery: Record<string, QueryScoreEntry> = {};
  for (const qs of withMatrix[0]?.queryScores || []) {
    if (qs.queryId) brandByQuery[qs.queryId] = qs;
  }
  return { latest, trendByName, brandByQuery, completedAt: audits[0]?.completedAt };
}
